import { defaultLayout } from 'shared';
import type { ScenarioContent, StepSchema } from 'shared';

// ============================================================
// Phase A-j デモシナリオ: 「付喪神の電話」
// LINE Dialog / 超常演出 / コメディエンディング / 履歴パネルの全機能デモ
// ============================================================

// --- ノード1: イントロ ---

const introScene: StepSchema = {
  id: 'step-scene-intro',
  type: 'scene',
  title: '不審な着信',
  notes: '',
  bg: {
    kind: 'color',
    color: '#1a1a2e',
    fit: 'cover',
    dimOpacity: 0.1
  },
  bgm: {
    src: undefined,
    volume: 0.6,
    loop: true
  }
};

const introDialog: StepSchema = {
  id: 'step-dialog-intro',
  type: 'dialog',
  title: '状況説明',
  speaker: { name: 'ナレーション', nameColor: '#94a3b8' },
  text: '深夜2時。あなたのスマートフォンにLINEの通知が届いた。知らない相手からのメッセージだ…。',
  portraits: {},
  layout: defaultLayout('dialog_overlay'),
  notes: ''
};

// --- ノード2: LINE Dialog（基本 + 超常演出デモ） ---

const lineDialogBasic: StepSchema = {
  id: 'step-line-basic',
  type: 'line_dialog',
  title: 'LINE: 不審なメッセージ',
  notes: '',
  chatTitle: '知らない番号',
  speakers: {
    unknown: {
      displayName: '???',
      side: 'other' as const,
      bubbleColor: '#FFFFFF',
      bubbleTextColor: '#333333'
    },
    player: {
      displayName: 'あなた',
      side: 'self' as const
    }
  },
  messages: [
    {
      senderKey: 'unknown',
      text: 'こんばんは。',
      delayMs: 500,
      typingIndicatorMs: 1200,
      kind: 'text' as const
    },
    {
      senderKey: 'unknown',
      text: 'あなたのことは知っています。',
      delayMs: 1000,
      typingIndicatorMs: 1500,
      kind: 'text' as const
    },
    {
      senderKey: 'unknown',
      text: '私の名前は…覚えていますか？',
      delayMs: 800,
      typingIndicatorMs: 1800,
      kind: 'text' as const,
      // 超常演出: テキスト文字化け
      supernaturalEffect: {
        effectId: 'text_corruption',
        trigger: 'after_delay' as const,
        triggerDelayMs: 2500,
        params: {
          startDelayMs: 0,
          flickerDurationMs: 800,
          burstDurationMs: 600,
          expansionFactor: 4,
        }
      }
    },
    {
      senderKey: 'unknown',
      text: '…すみません。通信が不安定なようで。',
      delayMs: 1200,
      typingIndicatorMs: 2000,
      kind: 'text' as const
    },
    {
      senderKey: 'unknown',
      text: '改めて。あなたに頼みたいことがあるんです。',
      delayMs: 800,
      typingIndicatorMs: 1500,
      kind: 'text' as const
    }
  ],
  reply: {
    mode: 'choices' as const,
    options: [
      { text: '誰ですか？', goto: 'node-line-detail' },
      { text: '何の用？', goto: 'node-line-detail' },
      { text: '（無視する）', goto: 'node-refuse-1' }
    ]
  },
  autoAdvance: false,
  autoAdvanceDelayMs: 2000,
  theme: {
    headerColor: '#06C755',
    chatBgColor: '#8CAAB9',
    chatBgPattern: 'line_default' as const
  }
};

// --- ノード3: LINE詳細会話（超常演出 + 自由入力デモ） ---

const lineDialogDetail: StepSchema = {
  id: 'step-line-detail',
  type: 'line_dialog',
  title: 'LINE: 依頼の詳細',
  notes: '',
  chatTitle: '???（自称・付喪神）',
  speakers: {
    tsukumogami: {
      displayName: '付喪神',
      side: 'other' as const,
      bubbleColor: '#e8d5ff',
      bubbleTextColor: '#2d1b4e'
    },
    player: {
      displayName: 'あなた',
      side: 'self' as const
    }
  },
  messages: [
    {
      senderKey: 'tsukumogami',
      text: '私は付喪神…長い年月を経た道具に宿る精霊です。',
      delayMs: 500,
      typingIndicatorMs: 1500,
      kind: 'text' as const
    },
    {
      senderKey: 'tsukumogami',
      text: '古い携帯電話に宿って、こうしてLINEを送っています。',
      delayMs: 800,
      typingIndicatorMs: 1800,
      kind: 'text' as const,
      // 超常演出: 吹き出しグリッチ
      supernaturalEffect: {
        effectId: 'glitch_bubble',
        trigger: 'after_delay' as const,
        triggerDelayMs: 1000,
        params: {
          intensity: 0.6,
          durationMs: 1500,
          colorShift: true
        }
      }
    },
    {
      senderKey: 'tsukumogami',
      text: '実は、私の宿主である携帯電話が壊されそうなんです。',
      delayMs: 1000,
      typingIndicatorMs: 2000,
      kind: 'text' as const
    },
    {
      senderKey: 'tsukumogami',
      text: '助けてほしいのですが…あなたの考えを聞かせてください。',
      delayMs: 800,
      typingIndicatorMs: 1500,
      kind: 'text' as const,
      // 超常演出: メッセージ書き換え
      supernaturalEffect: {
        effectId: 'message_rewrite',
        trigger: 'after_delay' as const,
        triggerDelayMs: 3000,
        params: {
          rewriteText: '助けて…もう時間がない…',
          rewriteDelayMs: 0,
          transitionType: 'glitch',
          transitionDurationMs: 400
        }
      }
    }
  ],
  reply: {
    mode: 'choices_and_free_input' as const,
    options: [
      { text: '助けます！', goto: 'node-post-line' },
      { text: '怪しい…もう少し詳しく', goto: 'node-post-line' }
    ],
    freeInput: {
      placeholder: '自由にメッセージを入力...',
      maxChars: 200,
      systemPrompt: 'あなたは付喪神（古い携帯電話に宿った精霊）です。丁寧だが少し不安げな口調で、助けを求めています。ユーザーの質問に短く答えてください。',
      routes: [
        { routeKey: 'default', label: '通常応答' },
        { routeKey: 'insight', label: '鋭い質問' }
      ],
      defaultRouteKey: 'default'
    }
  },
  autoAdvance: false,
  autoAdvanceDelayMs: 2000,
  theme: {
    headerColor: '#7c3aed',
    chatBgColor: '#1e1b3a',
    chatBgPattern: 'dark' as const
  }
};

// --- ノード3.5: LINE会話の振り返り（LINE履歴パネル確認用） ---

const postLineDialog: StepSchema = {
  id: 'step-post-line',
  type: 'dialog',
  title: '付喪神の話を振り返る',
  speaker: { name: 'ナレーション', nameColor: '#94a3b8' },
  text: '付喪神との会話を振り返ってみよう。\n（画面右下のLINE履歴ボタンで過去のやり取りを確認できます）',
  portraits: {},
  layout: defaultLayout('dialog_overlay'),
  notes: ''
};

// --- ノード4: 拒否1回目 ---

const refuseLineDialog1: StepSchema = {
  id: 'step-refuse-line-1',
  type: 'line_dialog',
  title: 'LINE: 再度の連絡',
  notes: '',
  chatTitle: '知らない番号',
  speakers: {
    unknown: {
      displayName: '???',
      side: 'other' as const,
      bubbleColor: '#FFFFFF',
      bubbleTextColor: '#333333'
    },
    player: {
      displayName: 'あなた',
      side: 'self' as const
    }
  },
  messages: [
    {
      senderKey: 'unknown',
      text: '…あの、無視しないでください。',
      delayMs: 1000,
      typingIndicatorMs: 2000,
      kind: 'text' as const
    },
    {
      senderKey: 'unknown',
      text: '本当に大事な話なんです。聞いてもらえませんか？',
      delayMs: 800,
      typingIndicatorMs: 1500,
      kind: 'text' as const
    }
  ],
  reply: {
    mode: 'choices' as const,
    options: [
      { text: 'わかった、聞くよ', goto: 'node-line-detail' },
      { text: '（また無視する）', goto: 'node-refuse-2' }
    ]
  },
  autoAdvance: false,
  autoAdvanceDelayMs: 2000,
  theme: {
    headerColor: '#06C755',
    chatBgColor: '#8CAAB9',
    chatBgPattern: 'line_default' as const
  }
};

// --- ノード5: 拒否2回目 ---

const refuseLineDialog2: StepSchema = {
  id: 'step-refuse-line-2',
  type: 'line_dialog',
  title: 'LINE: 最後のチャンス',
  notes: '',
  chatTitle: '知らない番号',
  speakers: {
    unknown: {
      displayName: '???',
      side: 'other' as const,
      bubbleColor: '#FFFFFF',
      bubbleTextColor: '#333333'
    },
    player: {
      displayName: 'あなた',
      side: 'self' as const
    }
  },
  messages: [
    {
      senderKey: 'unknown',
      text: 'えっ、また無視ですか？',
      delayMs: 800,
      typingIndicatorMs: 1500,
      kind: 'text' as const
    },
    {
      senderKey: 'unknown',
      text: '最後にもう一度だけ聞きます。お話、聞いてもらえますか？',
      delayMs: 1000,
      typingIndicatorMs: 2000,
      kind: 'text' as const
    }
  ],
  reply: {
    mode: 'choices' as const,
    options: [
      { text: 'わかったわかった', goto: 'node-line-detail' },
      { text: '（三度目の無視）', goto: 'node-comedy-ending' }
    ]
  },
  autoAdvance: false,
  autoAdvanceDelayMs: 2000,
  theme: {
    headerColor: '#06C755',
    chatBgColor: '#8CAAB9',
    chatBgPattern: 'line_default' as const
  }
};

// --- ノード6: グッドエンディング ---

const goodEndingStep: StepSchema = {
  id: 'step-good-ending',
  type: 'ending',
  title: 'グッドエンディング',
  notes: '',
  endingId: 'good_end',
  endingTitle: '付喪神の感謝',
  endingSubtitle: '古き友との出会い',
  endingType: 'good',
  message: '付喪神はあなたの優しさに感謝し、古い携帯電話の中から静かに微笑んだ。\nこれが、長い物語の始まりだった…。',
  showStats: true,
  stats: {
    showPlayTime: true,
    showCollectedItems: false,
    showCorrectAnswers: false
  },
  animation: 'fade',
  animationDurationMs: 1500,
  showReplayButton: true,
  showShareButton: false
};

// --- ノード7: コメディエンディング ---

const comedyEndingStep: StepSchema = {
  id: 'step-comedy-ending',
  type: 'ending',
  title: 'コメディエンディング',
  notes: '',
  endingId: 'comedy_refuse',
  endingTitle: 'COMEDY END',
  endingSubtitle: '既読スルーの達人',
  endingType: 'comedy',
  message: '3回連続で無視するとは…。\nなんでこのゲーム始めたん？',
  showStats: false,
  animation: 'fade',
  animationDurationMs: 1500,
  showReplayButton: false,
  showShareButton: false,
  comedyMode: {
    enabled: true,
    primaryButton: {
      label: '再度ゲームを開始する',
      action: 'replay',
      sizeFactor: 1.5,
      animation: 'bounce'
    },
    secondaryButton: {
      label: '本当に終了する',
      action: 'close',
      sizeFactor: 0.5,
      confirmation: {
        enabled: true,
        message: '本当に？もうこれ押したら本当に終わるけどいいのね？',
        confirmLabel: '本当に終了する',
        cancelLabel: 'やっぱりやめる'
      }
    },
    extras: {
      runawayButton: true,
      shakeOnConfirm: true,
      extraConfirmMessage: 'マジで？付喪神がかわいそうだと思わないの？？'
    }
  }
};

// ============================================================
// シナリオ構成
// ============================================================

export const mockScenario: ScenarioContent = {
  schemaVersion: 1,
  startNodeId: 'node-intro',
  nodes: [
    {
      id: 'node-intro',
      title: 'イントロ',
      steps: [introScene, introDialog],
      nextNodeId: 'node-line-first'
    },
    {
      id: 'node-line-first',
      title: 'LINE: 最初のメッセージ',
      steps: [lineDialogBasic],
      nextNodeId: null
    },
    {
      id: 'node-line-detail',
      title: 'LINE: 付喪神の詳細',
      steps: [lineDialogDetail],
      nextNodeId: null
    },
    {
      id: 'node-post-line',
      title: '振り返り（LINE履歴確認）',
      steps: [postLineDialog],
      nextNodeId: 'node-good-ending'
    },
    {
      id: 'node-refuse-1',
      title: '拒否1回目',
      steps: [refuseLineDialog1],
      nextNodeId: null
    },
    {
      id: 'node-refuse-2',
      title: '拒否2回目',
      steps: [refuseLineDialog2],
      nextNodeId: null
    },
    {
      id: 'node-good-ending',
      title: 'グッドエンディング',
      steps: [goodEndingStep],
      nextNodeId: null
    },
    {
      id: 'node-comedy-ending',
      title: 'コメディエンディング',
      steps: [comedyEndingStep],
      nextNodeId: null
    }
  ],
  meta: {
    title: 'Phase A-j デモ: 付喪神の電話'
  },
  startScreen: {
    title: '付喪神の電話',
    subtitle: 'Phase A-j 機能デモ',
    description: 'LINE Dialog・超常演出・コメディエンディング・履歴パネルの全機能をデモするシナリオです。',
    theme: 'dark',
    startButtonText: 'START',
    showAnimation: true,
    animationDurationMs: 1000
  },
  ui: {
    lineHistoryPanel: {
      enabled: true,
      defaultButtonPosition: 'bottom-right'
    }
  }
};
